import { IPublicPage } from 'src/interfaces/PublicPage';

export const MENU_INDENT_PX = 28;

export interface FlatMenuRow {
  id: string;
  page: IPublicPage;
  depth: number;
}

export interface MenuOrderUpdate {
  id: string;
  parentId: string | null;
  sortOrder: number;
}

export function normalizeParentId(parentId?: string | null): string | null {
  if (parentId == null || parentId === '') return null;
  return String(parentId);
}

export function flattenPagesToMenuRows(pages: IPublicPage[]): FlatMenuRow[] {
  const walk = (parentId: string | null, depth: number): FlatMenuRow[] =>
    pages
      .filter(p => normalizeParentId(p.menu.parentId) === parentId)
      .sort((a, b) => a.menu.sortOrder - b.menu.sortOrder)
      .flatMap(page => [{ id: page.id, page, depth }, ...walk(page.id, depth + 1)]);

  return walk(null, 0);
}

function getParentIdAtIndex(flat: Pick<FlatMenuRow, 'id' | 'depth'>[], index: number): string | null {
  const current = flat[index];
  if (!current) return null;
  const { depth } = current;
  if (depth === 0) return null;
  for (let i = index - 1; i >= 0; i -= 1) {
    const row = flat[i];
    if (row && row.depth === depth - 1) return row.id;
  }
  return null;
}

export function flatRowsToMenuUpdates(flat: Pick<FlatMenuRow, 'id' | 'depth'>[]): MenuOrderUpdate[] {
  return flat.map((row, index) => {
    const parentId = getParentIdAtIndex(flat, index);
    let sortOrder = 0;
    for (let j = 0; j < index; j += 1) {
      if (getParentIdAtIndex(flat, j) === parentId) sortOrder += 1;
    }
    return { id: row.id, parentId, sortOrder };
  });
}

function getMaxDepthAtIndex(flat: Pick<FlatMenuRow, 'depth'>[], index: number): number {
  if (index === 0) return 0;
  return (flat[index - 1]?.depth ?? 0) + 1;
}

function getSubtreeEnd(flat: FlatMenuRow[], start: number): number {
  const startRow = flat[start];
  if (!startRow) return start;
  const baseDepth = startRow.depth;
  let end = start;
  for (let i = start + 1; i < flat.length; i += 1) {
    const row = flat[i];
    if (!row || row.depth <= baseDepth) break;
    end = i;
  }
  return end;
}

/** Move dragged item plus its nested children; optional horizontal nest via dragOffsetX. */
export function moveMenuSubtree(
  flat: FlatMenuRow[],
  activeId: string,
  overId: string,
  dragOffsetX: number,
): FlatMenuRow[] | null {
  const fromIndex = flat.findIndex(r => r.id === activeId);
  const overIndexOriginal = flat.findIndex(r => r.id === overId);
  if (fromIndex < 0 || overIndexOriginal < 0) return null;

  const subtreeEnd = getSubtreeEnd(flat, fromIndex);
  const block = flat.slice(fromIndex, subtreeEnd + 1);
  const rest = [...flat.slice(0, fromIndex), ...flat.slice(subtreeEnd + 1)];

  const overIndex = rest.findIndex(r => r.id === overId);
  if (overIndex < 0) return null;

  const insertAt = fromIndex < overIndexOriginal ? overIndex + 1 : overIndex;
  const moved = [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];

  const rootIndex = moved.findIndex(r => r.id === activeId);
  const dragDepthDelta = Math.round(dragOffsetX / MENU_INDENT_PX);
  const maxDepth = getMaxDepthAtIndex(moved, rootIndex);
  const blockRoot = block[0];
  if (!blockRoot) return null;
  const newRootDepth = Math.max(0, Math.min(maxDepth, blockRoot.depth + dragDepthDelta));
  const depthDelta = newRootDepth - blockRoot.depth;
  const blockEnd = rootIndex + block.length - 1;

  return moved.map((row, i) => {
    if (i < rootIndex || i > blockEnd) return row;
    return { ...row, depth: row.depth + depthDelta };
  });
}

export function canIndentRow(flat: FlatMenuRow[], index: number): boolean {
  if (index <= 0) return false;
  return (flat[index]?.depth ?? 0) < getMaxDepthAtIndex(flat, index);
}

export function canOutdentRow(flat: FlatMenuRow[], index: number): boolean {
  return (flat[index]?.depth ?? 0) > 0;
}

/** Indent/outdent a row (and descendants) based on horizontal drag pixels. */
export function applyHorizontalDragOffset(
  flat: FlatMenuRow[],
  index: number,
  offsetX: number,
): FlatMenuRow[] {
  const steps = Math.round(offsetX / MENU_INDENT_PX);
  if (steps === 0) return flat;

  let result = flat;
  if (steps > 0) {
    for (let i = 0; i < steps; i += 1) {
      if (!canIndentRow(result, index)) break;
      result = indentRow(result, index);
    }
  } else {
    for (let i = 0; i < Math.abs(steps); i += 1) {
      if (!canOutdentRow(result, index)) break;
      result = outdentRow(result, index);
    }
  }
  return result;
}

export function projectDepthFromDragOffset(
  flat: FlatMenuRow[],
  index: number,
  offsetX: number,
): number {
  const maxDepth = getMaxDepthAtIndex(flat, index);
  const steps = Math.round(offsetX / MENU_INDENT_PX);
  return Math.max(0, Math.min(maxDepth, (flat[index]?.depth ?? 0) + steps));
}

function shiftDescendantDepths(
  flat: FlatMenuRow[],
  startIndex: number,
  baseDepth: number,
  delta: number,
): FlatMenuRow[] {
  return flat.map((row, i) => {
    if (i <= startIndex) return row;
    if (row.depth > baseDepth) return { ...row, depth: row.depth + delta };
    return row;
  });
}

export function indentRow(flat: FlatMenuRow[], index: number): FlatMenuRow[] {
  if (!canIndentRow(flat, index)) return flat;
  const baseDepth = flat[index]?.depth ?? 0;
  const withIndent = flat.map((row, i) =>
    i === index ? { ...row, depth: row.depth + 1 } : row,
  );
  return shiftDescendantDepths(withIndent, index, baseDepth, 1);
}

export function outdentRow(flat: FlatMenuRow[], index: number): FlatMenuRow[] {
  if (!canOutdentRow(flat, index)) return flat;
  const baseDepth = flat[index]?.depth ?? 0;
  const withOutdent = flat.map((row, i) =>
    i === index ? { ...row, depth: row.depth - 1 } : row,
  );
  return shiftDescendantDepths(withOutdent, index, baseDepth, -1);
}
